// "O que você fez" — um texto livre, ligado a um item da lista de atividades
// da empresa.
//
// Mesmo desenho do DescricaoComboBox das composições do comercial (pedido do
// Lucas, 23/09/2026): UM campo de texto; o 📚 abre a lista da empresa;
// escolher dela preenche o texto e deixa o vínculo à mostra (🔗 Relatório ×);
// editar o texto depois NÃO desfaz o vínculo — "Relatório — capítulo 3"
// continua contando como Relatório. O × tira só o vínculo, nunca o texto.
//
// Uma diferença de propósito em relação às composições: lá o catálogo só abre
// pelo 📚. Aqui a lista também aparece ENQUANTO a pessoa digita, e o texto
// que bate exatamente com um item se liga sozinho. Sem isso, quem escreve
// "campo" nunca abriria o 📚 e o vínculo — que é o que permite somar "quanto
// do contrato foi campo" — ficaria vazio em quase todo lançamento.
//
// HTML inline, sem antd, para servir igual no atalho do cabeçalho (que é todo
// HTML) e no modal da tela de horas.
import { useEffect, useRef, useState } from 'react';

export type AtividadeTipo = { id: number; nome: string; ativo?: boolean };

const normal = (s: string) => s.trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

export default function AtividadeCampo({
  texto, atividadeId, catalogo, onTexto, onAtividade, compacto = false, autoFoco = false,
}: {
  texto: string;
  atividadeId: number | null;
  catalogo: AtividadeTipo[];
  onTexto: (v: string) => void;
  onAtividade: (id: number | null) => void;
  compacto?: boolean;
  autoFoco?: boolean;
}) {
  // 'lista' = aberta pelo 📚, com busca própria; 'sugestao' = aberta pelo
  // próprio texto que a pessoa está digitando.
  const [modo, setModo] = useState<null | 'lista' | 'sugestao'>(null);
  const [busca, setBusca] = useState('');
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const caixa = useRef<HTMLDivElement>(null);

  // Item desativado continua no chip de quem já estava ligado a ele — sumir
  // o nome do lançamento antigo seria apagar informação.
  const vinculado = atividadeId != null ? catalogo.find(a => a.id === atividadeId) || null : null;
  const ativos = catalogo.filter(a => a.ativo !== false);
  const termo = normal(modo === 'lista' ? busca : texto);
  const filtrados = termo ? ativos.filter(a => normal(a.nome).includes(termo)) : ativos;
  const mostrar = modo === 'lista' || (modo === 'sugestao' && !vinculado && !!termo && filtrados.length > 0);

  // Posição fixa calculada: o atalho do cabeçalho e o corpo do modal cortam
  // (overflow) um dropdown absoluto. Largura e canto esquerdo presos à tela,
  // para o celular não perder metade da lista.
  useEffect(() => {
    if (!mostrar) return;
    const reposicionar = () => {
      const el = caixa.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const altura = 260;
      const top = window.innerHeight - r.bottom < altura && r.top > altura ? r.top - altura - 4 : r.bottom + 4;
      const width = Math.min(Math.max(r.width, 240), window.innerWidth - 24);
      const left = Math.max(12, Math.min(r.left, window.innerWidth - width - 12));
      setPos({ top, left, width });
    };
    reposicionar();
    window.addEventListener('scroll', reposicionar, true);
    window.addEventListener('resize', reposicionar);
    return () => {
      window.removeEventListener('scroll', reposicionar, true);
      window.removeEventListener('resize', reposicionar);
    };
  }, [mostrar]);

  const fs = compacto ? 12 : 14;

  function escolher(a: AtividadeTipo) {
    onAtividade(a.id);
    // Texto vazio, ou só o começo do nome que a pessoa estava digitando, ou o
    // nome de outro item da lista → recebe o nome escolhido. Texto que ela já
    // escreveu por extenso fica: ela está só dizendo de que tipo é aquilo.
    const t = normal(texto);
    if (!t || normal(a.nome).includes(t) || catalogo.some(c => normal(c.nome) === t)) onTexto(a.nome);
    setModo(null);
    setBusca('');
  }

  function digitar(v: string) {
    onTexto(v);
    // Bateu exatamente com um item da lista → liga nele, mesmo que houvesse
    // outro vínculo: quem escreve "Relatório / produto técnico" por extenso
    // está dizendo que é isso, e o vínculo antigo gravaria a atividade errada.
    const igual = ativos.find(a => normal(a.nome) === normal(v));
    if (igual && igual.id !== atividadeId) onAtividade(igual.id);
    setModo('sugestao');
  }

  return (
    <div ref={caixa} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: 4 }}>
        <input
          value={texto} autoFocus={autoFoco}
          onChange={e => digitar(e.target.value)}
          onFocus={() => setModo(m => m || 'sugestao')}
          // Atraso curto: o clique numa sugestão chega depois do blur.
          onBlur={() => setTimeout(() => setModo(m => (m === 'sugestao' ? null : m)), 150)}
          onKeyDown={e => {
            if (e.key === 'Escape') setModo(null);
            if (e.key === 'Enter' && modo === 'sugestao' && !vinculado && filtrados.length === 1) {
              e.preventDefault(); escolher(filtrados[0]);
            }
          }}
          placeholder="O que você fez? (opcional)"
          maxLength={500}
          style={{
            flex: 1, minWidth: 0, fontSize: fs, padding: compacto ? '6px 8px' : '5px 11px',
            borderRadius: 6, border: '1px solid #d9d9d9', outline: 'none',
          }} />
        <button
          type="button" onClick={() => { setModo(m => (m === 'lista' ? null : 'lista')); setBusca(''); }}
          title="Escolher da lista de atividades da empresa"
          aria-label="Lista de atividades"
          style={{
            padding: compacto ? '0 8px' : '0 10px', fontSize: fs, borderRadius: 6, cursor: 'pointer',
            border: `1px solid ${vinculado ? '#0930D0' : '#d9d9d9'}`,
            background: vinculado ? '#E7ECFD' : '#fff',
          }}>📚</button>
      </div>

      {vinculado && (
        <div style={{ marginTop: 3 }}>
          <span style={{
            background: '#E7ECFD', color: '#0930D0', padding: '1px 6px', borderRadius: 4,
            fontSize: compacto ? 11 : 12, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4,
          }}>
            🔗 {vinculado.nome}{vinculado.ativo === false ? ' (fora da lista)' : ''}
            <button type="button" onClick={() => onAtividade(null)} title="Tirar o vínculo (o texto fica)"
              style={{ background: 'none', border: 'none', color: '#0930D0', cursor: 'pointer',
                       padding: 0, fontSize: 13, lineHeight: 1 }}>×</button>
          </span>
        </div>
      )}

      {mostrar && pos && (
        <>
          {modo === 'lista' && (
            <div onClick={() => setModo(null)} style={{ position: 'fixed', inset: 0, zIndex: 2000 }} />
          )}
          <div style={{
            position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 2001,
            background: '#fff', border: '0.5px solid #0930D0', borderRadius: 6,
            boxShadow: '0 4px 16px rgba(0,0,0,0.18)', maxHeight: 260,
            display: 'flex', flexDirection: 'column',
          }}>
            {modo === 'lista' && (
              <input
                autoFocus placeholder="Buscar atividade…" value={busca}
                onChange={e => setBusca(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && filtrados.length) { e.preventDefault(); escolher(filtrados[0]); }
                  if (e.key === 'Escape') setModo(null);
                }}
                style={{ margin: 6, fontSize: 12, padding: '5px 8px', borderRadius: 4, border: '1px solid #d9d9d9' }} />
            )}
            <div style={{ overflowY: 'auto' }}>
              {filtrados.length === 0 && (
                <div style={{ padding: 12, fontSize: 12, color: '#666', textAlign: 'center' }}>
                  {ativos.length === 0
                    ? 'A lista de atividades ainda não está disponível. Escreva no campo — o texto é gravado.'
                    : `Nada na lista com "${busca}". Escreva no campo mesmo — quem cuida da lista pode incluir depois.`}
                </div>
              )}
              {filtrados.map(a => (
                <button key={a.id} type="button"
                  // mousedown e não click: o blur do campo vem antes do click.
                  onMouseDown={e => { e.preventDefault(); escolher(a); }}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px',
                    fontSize: 12, border: 'none', cursor: 'pointer',
                    background: a.id === atividadeId ? '#F0F3FF' : 'transparent',
                    borderBottom: '0.5px solid #f3f3f3',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#F0F1F5')}
                  onMouseLeave={e => (e.currentTarget.style.background = a.id === atividadeId ? '#F0F3FF' : 'transparent')}>
                  {modo === 'sugestao' && <span style={{ color: '#0930D0', marginRight: 6 }}>🔗</span>}
                  {a.nome}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
